import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { IHttpError, RootState } from "Types";

import {
  addProgressAction,
  isActionInProgressSelector,
  removeProgressAction,
} from "../utils/loader/progress.slice";
import { httpGet } from "../services/api/axiosHelpers";
import { handleAPIError } from "../utils/error.util";
import type { ConfigState, ConfigApiResponse } from "./config.types";
import { globalUrls } from "../services/api/api-urls";

const initialState: ConfigState = {
  data: { vantagePath: null },
  status: "idle",
  error: null,
};

export const fetchConfig = createAsyncThunk<
  ConfigApiResponse,
  { useDummyToken?: boolean; _signal?: AbortSignal },
  {
    rejectValue: IHttpError;
  }
>(
  "config/fetchConfig",
  async ({ useDummyToken, _signal }, { dispatch, rejectWithValue, signal }) => {
    try {
      dispatch(addProgressAction({ action: "fetchConfig" }));
      const response = await httpGet({
        useDummyToken,
        url: globalUrls.config,
        signal: _signal || signal,
      });
      return response.data;
    } catch (e: unknown) {
      return handleAPIError<ReturnType<typeof rejectWithValue>>({
        e,
        dispatch,
        rejectWithValue,
        source: "config.slice.ts - fetchConfig",
      });
    } finally {
      dispatch(removeProgressAction("fetchConfig"));
    }
  },
);

export const configSlice = createSlice({
  name: "config",
  initialState,
  reducers: {
    clear: () => {
      return initialState;
    },
    clearConfigState: () => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchConfig.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchConfig.fulfilled, (state, action) => {
        state.status = "idle";
        state.data = action.payload;
      })
      .addCase(fetchConfig.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error;
      });
  },
});

export const { clear } = configSlice.actions;

export const selectConfigState = (state: RootState): ConfigState =>
  state.config;

export const isFetchConfigInProgressSelector = (state: RootState) =>
  isActionInProgressSelector(state, "fetchConfig");

export default configSlice.reducer;
